"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { InfoCircle } from "iconoir-react";
import { useLocale, useTranslations } from "next-intl";
import type { ReactNode } from "react";
import type { DefaultValues } from "react-hook-form";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItemWithoutDiv,
  UnstyledFormLabel,
} from "@/components/ui/form";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { LOCALE_CURRENCY_SYMBOL } from "@/lib/locale-prefix";

import {
  CurrencyInput,
  LabelCell,
  NumberDisplayCell,
  Table,
  TableHeader,
  Tr,
} from "./shared";

function FieldLabel({
  children,
  tooltip,
}: {
  children: ReactNode;
  tooltip: string;
}) {
  return (
    <span className="inline-flex items-center gap-1">
      <UnstyledFormLabel>{children}</UnstyledFormLabel>
      <Tooltip>
        <TooltipTrigger
          type="button"
          aria-label={tooltip}
          className="text-text-label hover:text-pulse-700 inline-flex shrink-0 items-center"
        >
          <InfoCircle className="size-3.5" />
        </TooltipTrigger>
        <TooltipContent>{tooltip}</TooltipContent>
      </Tooltip>
    </span>
  );
}

const formSchema = z.object({
  cogs: z.number().positive(),
  beginningInventory: z.number().positive(),
  endingInventory: z.number().positive(),
});

const defaultValues = {
  cogs: 1_200_000,
  beginningInventory: 280_000,
  endingInventory: 320_000,
} satisfies DefaultValues<z.infer<typeof formSchema>>;

export function InventoryTurnoverRatioCalculator() {
  const t = useTranslations("Calculators");
  const tCommon = useTranslations("Common");
  const locale = useLocale();
  const currencySymbol = LOCALE_CURRENCY_SYMBOL[locale] ?? "$";
  const form = useForm<z.infer<typeof formSchema>>({
    mode: "onChange",
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  const cogs = form.watch("cogs");
  const averageInventory =
    (form.watch("beginningInventory") + form.watch("endingInventory")) / 2;
  const turnoverRatio =
    averageInventory > 0 && cogs > 0 ? cogs / averageInventory : null;
  const turnoverDays = turnoverRatio ? Math.round(365 / turnoverRatio) : null;
  const notAvailable = tCommon("notAvailable");

  return (
    <TooltipProvider>
      <Form {...form}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          <TableHeader>
            <p className="text-body-md sticky top-0 font-semibold text-white">
              {t("inventoryTurnoverRatio")}
            </p>
          </TableHeader>
          <Table>
            <colgroup>
              <col className="w-4/6 md:w-3/4" />
              <col className="w-2/6 md:w-1/4" />
            </colgroup>
            <tbody className="bg-surface">
              <FormField
                control={form.control}
                name="cogs"
                render={({ field }) => (
                  <FormItemWithoutDiv>
                    <Tr>
                      <LabelCell>
                        <FieldLabel tooltip={t("cogsTooltip")}>
                          {t("cogs")}
                        </FieldLabel>
                      </LabelCell>
                      <NumberDisplayCell>
                        <FormControl>
                          <CurrencyInput {...field} prefix={currencySymbol} />
                        </FormControl>
                      </NumberDisplayCell>
                    </Tr>
                  </FormItemWithoutDiv>
                )}
              />

              <FormField
                control={form.control}
                name="beginningInventory"
                render={({ field }) => (
                  <FormItemWithoutDiv>
                    <Tr>
                      <LabelCell>
                        <FieldLabel tooltip={t("beginningInventoryTooltip")}>
                          {t("beginningInventory")}
                        </FieldLabel>
                      </LabelCell>
                      <NumberDisplayCell>
                        <FormControl>
                          <CurrencyInput {...field} prefix={currencySymbol} />
                        </FormControl>
                      </NumberDisplayCell>
                    </Tr>
                  </FormItemWithoutDiv>
                )}
              />

              <FormField
                control={form.control}
                name="endingInventory"
                render={({ field }) => (
                  <FormItemWithoutDiv>
                    <Tr className="border-b">
                      <LabelCell>
                        <FieldLabel tooltip={t("endingInventoryTooltip")}>
                          {t("endingInventory")}
                        </FieldLabel>
                      </LabelCell>
                      <NumberDisplayCell>
                        <FormControl>
                          <CurrencyInput {...field} prefix={currencySymbol} />
                        </FormControl>
                      </NumberDisplayCell>
                    </Tr>
                  </FormItemWithoutDiv>
                )}
              />

              <Tr>
                <LabelCell>{t("averageInventory")}</LabelCell>
                <NumberDisplayCell>{averageInventory}</NumberDisplayCell>
              </Tr>

              <Tr className="border-b-0">
                <LabelCell className="bg-pulse-50 rounded-l-lg font-bold">
                  {t("turnoverRatio")}
                </LabelCell>
                <NumberDisplayCell className="bg-pulse-50 rounded-r-lg font-bold">
                  {turnoverRatio ?? notAvailable}
                </NumberDisplayCell>
              </Tr>

              <Tr>
                <LabelCell className="bg-pulse-50 rounded-l-lg font-bold">
                  {t("turnoverDays")}
                </LabelCell>
                <NumberDisplayCell className="bg-pulse-50 rounded-r-lg font-bold">
                  {turnoverDays ?? notAvailable}
                </NumberDisplayCell>
              </Tr>
            </tbody>
            <tfoot className="py-4">
              <Tr className="border-x-0 border-b-0 md:border-x-0">
                <td colSpan={2} className="px-5 py-3 text-center">
                  <button
                    type="submit"
                    className="hidden"
                    onClick={(e) => e.preventDefault()}
                  />
                  <Button
                    onClick={() => {
                      Object.entries(defaultValues).forEach(([key, value]) => {
                        form.setValue(key as keyof typeof defaultValues, value);
                      });
                    }}
                    variant="link"
                    size="sm"
                    className="text-body"
                  >
                    {tCommon("clearResults")}
                  </Button>
                </td>
              </Tr>
            </tfoot>
          </Table>
        </form>
      </Form>
    </TooltipProvider>
  );
}
